import { type CSSProperties, forwardRef, useCallback } from "react";

import { cx, styled } from "simplydo/react/styled";
import { H3 } from "simplydo/react/ui/header";
import { Icon, IconData } from "simplydo/react/ui/icon";
import { Label } from "simplydo/react/ui/label";
import { Popup } from "simplydo/react/ui/popup";
import type { UiColor } from "simplydo/react/ui/utils/colors";

import util from "utils/utils";

import { getRoleIcon } from "components/lib/Roles/roleIcons";

const StyledTitle = styled.div`
  display: block;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-weight: bold !important;
  font-size: 13px !important;
  line-height: 15px !important;
  text-decoration: none !important;
  width: fit-content;
  max-width: 100%;
  position: relative;
  margin-right: 4px;
  color: black !important;

  &.is-compact {
    font-size: 12px !important;
    line-height: 14px !important;
  }

  &.is-very-compact {
    font-weight: inherit !important;
    font-size: 1em !important;
    line-height: 1lh !important;
    margin-right: 0;
    color: inherit !important;
  }

  &.is-inverted {
    color: white !important;
  }
`;

const StyledAvatar = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  transform: none;
  height: 35px;
  width: 35px;
  min-height: 35px;
  min-width: 35px;
  border-radius: 50%;
  background-color: #ededed;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  margin-right: 8px !important;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.2);

  &.has-image {
    background-color: transparent;
  }

  &.is-compact {
    height: 24px;
    width: 24px;
    min-height: 24px;
    min-width: 24px;
  }

  &.is-very-compact {
    display: inline-block;
    transform: translateY(10%);
    height: 1em;
    width: 1em;
    min-height: 1em;
    min-width: 1em;
    margin-right: 4px !important;
    box-shadow: none;
  }

  &.has-custom-radius {
    border-radius: var(--chip-avatar-radius);
  }

  &.has-custom-background-size {
    background-size: var(--chip-avatar-background-size);
  }
`;

const StyledChip = styled.div`
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  border-radius: 50px;
  padding: 3px 12px 3px 3px;
  transition:
    background 0.1s,
    box-shadow 0.1s;

  &.is-very-compact {
    display: inline;
    padding: 0px;
  }

  &.is-inverted {
    background: rgba(0, 3, 45, 0.3);
  }

  &.has-custom-radius {
    border-radius: var(--chip-border-radius);
  }

  &.is-interactive:not(.is-very-compact):hover {
    box-shadow:
      0 0 1px 1px rgba(255, 255, 255, 0.1),
      1px 1px 1px 0 rgba(0, 0, 0, 0.1);

    ${StyledAvatar} {
      box-shadow: 0 0 4px rgba(0, 0, 0, 0.12);
    }
  }

  &.is-interactive.is-inverted:not(.is-very-compact):hover {
    box-shadow:
      0 0 1px 1px rgba(255, 255, 255, 0.1),
      1px 1px 1px 0 rgba(0, 0, 0, 0.4);
  }

  &.is-interactive:not(.is-very-compact):active {
    box-shadow:
      0 0 3px 1px rgba(0, 0, 0, 0.05) inset,
      0 1px 2px 0 rgba(0, 0, 0, 0.15) inset;
    background: rgba(0, 0, 0, 0.04);

    ${StyledAvatar} {
      box-shadow: 0 0 2px rgba(0, 0, 0, 0.05);
    }
  }

  &.is-interactive.is-inverted:not(.is-very-compact):active {
    background: rgba(0, 3, 45, 0.37);
  }
`;

const ChipTextContainer = styled.div`
  display: inline-flex;
  flex-direction: column;
  user-select: none;
  text-wrap: nowrap;
  min-width: 0px;
  max-width: calc(100% - 4px - 0.9em);
  flex: 1 1 auto;

  &.is-very-compact {
    max-width: 100%;
  }
`;

const StyledPostText = styled.span`
  cursor: default;
  font-size: 13px !important;
  vertical-align: middle;
  color: black;
  margin-left: 6px;

  &.is-inverted {
    color: white;
  }
`;

const StyledDescription = styled.p`
  font-size: 11px !important;
  line-height: 12px;
  min-height: 10px;
  min-width: 1px;
  text-overflow: ellipsis;
  overflow: hidden;
  font-weight: normal !important;
  color: black !important;
  margin: 0px !important;

  &.is-compact {
    font-size: 10px !important;
  }

  &.is-inverted {
    color: white !important;
  }

  &.is-trigger {
    max-width: 150px;
  }
`;

const ActionIcon = styled(Icon)`
  &&&& {
    color: gray;
    margin: 0;
    font-size: 1.2em;
    margin-left: 0px;
    margin-right: -8.75px;
    cursor: pointer;
    padding: 18px;
    border-radius: 50%;
    position: relative;

    &::before {
      position: absolute;
      top: 9px;
      bottom: 0;
      left: 0;
      right: 0;
    }

    &:hover {
      background: gray;
      color: white;
    }

    &.is-inverted {
      color: white;

      &:hover {
        background: white;
        color: gray;
      }
    }

    &.is-compact {
      padding: 12px;

      &::before {
        top: 3px;
      }
    }
  }
`;

const PopupContent = styled.div`
  display: flex;
  flex-direction: row;
  min-width: 300px;
`;

const PopupLargerImage = styled.div`
  min-width: 70px;
  min-height: 70px;
  width: 70px;
  height: 70px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${({ theme }) => theme.primaryColour};
  border-radius: 50%;
  background-color: #ededed;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  margin-right: 10px;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.2);

  &.has-image {
    background-color: transparent;
  }

  &.has-custom-radius {
    border-radius: var(--chip-avatar-radius);
  }

  &.has-custom-background-size {
    background-size: var(--chip-avatar-background-size);
  }
`;

const PopupInfo = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
`;

const PopupFooter = styled.div`
  background: #f9fafb;
  padding: 0.833em 1em 0.833em 1em;
  border-top: 1px solid rgba(34, 36, 38, 0.15);
  text-align: right;
  margin: 0.833em -1em -0.833em -1em;
  border-radius: 0 0 0.5em 0.5em;

  .ui.button {
    margin-right: 0;
  }
`;

const getCompactClass = (compact?: boolean | "very") =>
  compact === "very" ? "is-very-compact" : compact ? "is-compact" : undefined;

const getAvatarStyle = ({
  imageUrl,
  avatarBorderRadius,
  avatarBackgroundSize,
}: {
  imageUrl?: string;
  avatarBorderRadius?: number;
  avatarBackgroundSize?: string;
}): CSSProperties => ({
  ...(imageUrl ? { backgroundImage: `url(${imageUrl})` } : {}),
  ...(avatarBorderRadius !== undefined
    ? { "--chip-avatar-radius": `${avatarBorderRadius}px` }
    : {}),
  ...(avatarBackgroundSize
    ? { "--chip-avatar-background-size": avatarBackgroundSize }
    : {}),
});

export type ChipConfiguration = {
  style?: CSSProperties;
  avatarBorderRadius?: number;
  avatarBackgroundSize?: string;
  chipBorderRadius?: number;
  inverted?: boolean;
  content?: React.ReactNode;
  labels?: { name?: string; icon?: { name?: string; colour?: string } }[];

  precedingText?: string;
  postText?: string;
  target?: "_blank";

  onClick?: ((e: React.MouseEvent<HTMLDivElement>) => void) | undefined;
  disabled?: boolean;
  compact?: boolean | "very";
  actionIcon?: IconData;
  actionClick?: () => void;
  actionTooltip?: string;
  footer?: React.ReactNode;
};

type BaseChipProps = (
  | { imageUrl: string; icon?: never }
  | { imageUrl?: never; icon?: IconData }
  | { imageUrl?: never; icon?: never }
) & {
  title: React.ReactNode;
  largeTitle?: React.ReactNode;
  subtitle?: string;
} & ChipConfiguration;

const BaseChip = forwardRef<HTMLAnchorElement, BaseChipProps>(
  (
    {
      imageUrl,
      icon,
      avatarBorderRadius,
      avatarBackgroundSize,
      chipBorderRadius,
      style,
      disabled,
      inverted,
      title,
      largeTitle,
      subtitle,
      content,
      labels,
      precedingText,
      postText,
      onClick,
      compact,
      actionIcon,
      actionClick,
      actionTooltip,
      footer,
    },
    ref,
  ) => {
    const handleClickWithDefault = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        if (onClick) {
          e.stopPropagation();
          e.preventDefault();
          onClick(e);
        }
      },
      [onClick],
    );

    const imageUrlFallback = imageUrl
      ? util.mixinCssUrlFallback(imageUrl, util.avatarUrl() ?? "")
      : undefined;

    const compactClass = getCompactClass(compact);
    const avatarStyle = getAvatarStyle({
      imageUrl: imageUrlFallback,
      avatarBorderRadius,
      avatarBackgroundSize,
    });
    const chipStyle = {
      ...(chipBorderRadius !== undefined
        ? { "--chip-border-radius": `${chipBorderRadius}px` }
        : {}),
    } as CSSProperties;

    return (
      <Popup
        wide="very"
        on="click"
        disabled={disabled}
        style={{ maxWidth: 400 }}
        trigger={
          <span
            className="chip"
            ref={ref}
            style={{
              display: "inline-flex",
              flexWrap: "wrap",
              alignItems: "center",
              minWidth: 0,
              whiteSpace: "nowrap",
              color: "inherit",
              maxWidth: "100%",
              verticalAlign: compact === "very" ? "top" : "middle",
              cursor: onClick && !disabled ? "pointer" : "default",
              ...(style || {}),
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <StyledChip
              onClick={handleClickWithDefault}
              as={compact === "very" ? "span" : "div"}
              className={cx(
                compactClass,
                inverted && "is-inverted",
                chipBorderRadius !== undefined && "has-custom-radius",
                compact !== "very" && !disabled && "is-interactive",
              )}
              style={chipStyle}
            >
              <StyledAvatar
                as={compact === "very" ? "span" : "div"}
                className={cx(
                  compactClass,
                  imageUrlFallback && "has-image",
                  avatarBorderRadius !== undefined && "has-custom-radius",
                  avatarBackgroundSize && "has-custom-background-size",
                )}
                style={avatarStyle}
              >
                {!imageUrl && icon ? (
                  <Icon
                    icon={icon}
                    style={{ marginLeft: 2, width: 10 }}
                    size={compact === "very" ? "small" : undefined}
                  />
                ) : null}
              </StyledAvatar>
              <ChipTextContainer
                as={compact === "very" ? "span" : "div"}
                className={cx(compactClass)}
              >
                {compact !== "very" && precedingText ? (
                  <StyledDescription
                    className={cx(inverted && "is-inverted", "is-trigger")}
                  >
                    {precedingText}
                  </StyledDescription>
                ) : null}
                <StyledTitle
                  as={compact === "very" ? "span" : "div"}
                  className={cx(compactClass, inverted && "is-inverted")}
                >
                  {title}
                </StyledTitle>
                {compact !== "very" && subtitle ? (
                  <StyledDescription
                    className={cx(
                      compactClass,
                      inverted && "is-inverted",
                      "is-trigger",
                    )}
                  >
                    {subtitle}
                  </StyledDescription>
                ) : null}
              </ChipTextContainer>
              {compact !== "very" ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    flexWrap: "wrap",
                    marginLeft: 4,
                    maxHeight: 30,
                    rowGap: 3,
                    minWidth: 0,
                    overflow: "hidden",
                    flex: "1 1 auto",
                  }}
                >
                  {labels?.map((label) =>
                    label.icon ? (
                      <Icon
                        key={`${label.name}-${label.icon?.name}-${label.icon?.colour}`}
                        icon={getRoleIcon(label.icon.name)}
                        color={label.icon.colour as UiColor}
                        size="small"
                      />
                    ) : null,
                  )}
                </div>
              ) : null}
              {compact !== "very" && actionIcon && actionClick ? (
                <Popup
                  disabled={!actionTooltip}
                  trigger={
                    <ActionIcon
                      icon={actionIcon}
                      className={cx(
                        inverted && "is-inverted",
                        compact && "is-compact",
                      )}
                      onClick={(e: React.MouseEvent) => {
                        e.preventDefault();
                        e.stopPropagation();
                        actionClick();
                      }}
                    />
                  }
                >
                  {actionTooltip}
                </Popup>
              ) : null}
            </StyledChip>
            {postText && (
              <StyledPostText className={cx(inverted && "is-inverted")}>
                {postText}
              </StyledPostText>
            )}
          </span>
        }
      >
        {
          <>
            <PopupContent>
              <PopupLargerImage
                className={cx(
                  imageUrl && "has-image",
                  avatarBorderRadius !== undefined && "has-custom-radius",
                  avatarBackgroundSize && "has-custom-background-size",
                )}
                style={getAvatarStyle({
                  imageUrl,
                  avatarBorderRadius,
                  avatarBackgroundSize,
                })}
              >
                {!imageUrl && icon ? (
                  <Icon icon={icon} size="large" style={{ marginLeft: 4 }} />
                ) : null}
              </PopupLargerImage>
              <PopupInfo>
                <H3 style={{ marginBottom: 2, marginTop: 0, display: "flex" }}>
                  {largeTitle ?? title}
                </H3>
                <div style={{ marginBottom: 4 }}>
                  {labels?.map((label) => (
                    <Label
                      circular
                      color={label.icon?.colour as UiColor}
                      size="small"
                      key={`${label.name}-${label.icon?.name}-${label.icon?.colour}`}
                    >
                      {label.icon ? (
                        <Icon
                          size="small"
                          icon={getRoleIcon(label.icon.name)}
                          color={label.icon.colour as UiColor}
                        />
                      ) : null}
                      {label.name}
                    </Label>
                  ))}
                </div>
                {subtitle ? (
                  <p style={{ marginBottom: 6, marginTop: 0 }}>{subtitle}</p>
                ) : null}
                {content}
              </PopupInfo>
            </PopupContent>
            {footer ? <PopupFooter>{footer}</PopupFooter> : null}
          </>
        }
      </Popup>
    );
  },
);

BaseChip.displayName = "BaseChip";

export default BaseChip;
